import { finishes } from './csw-finish-identities-v27.js';

const byId = new Map(finishes.map((finish) => [finish.selection_id, finish]));
const byManufacturer = new Map(finishes.map((finish) => [finish.manufacturer_identification, finish]));
const legacyKey = 'Manufacturer Finish Reference';

export function finishProperties(selectionId, existing = {}) {
  const finish = byId.get(selectionId);
  if (!finish || !finish.customer_display_name || /\p{Script=Han}/u.test(finish.customer_display_name)) {
    throw new Error('Unknown finish identity');
  }
  const properties = {
    ...existing,
    Finish: finish.customer_display_name,
    '_Finish Selection ID': finish.selection_id,
    '_Manufacturer Finish Reference': finish.manufacturer_identification,
  };
  delete properties[legacyKey];
  return properties;
}

function legacyFinish(item) {
  const reference = item.properties?.[legacyKey];
  if (!reference) return null;
  const finish = byManufacturer.get(reference);
  const selectionId = item.properties['_Finish Selection ID'];
  const privateReference = item.properties['_Manufacturer Finish Reference'];
  if (!finish || (selectionId && selectionId !== finish.selection_id) || (privateReference && privateReference !== reference)) {
    throw new Error('Legacy finish requires confirmation');
  }
  return finish;
}

export async function migrateLegacyCart(request) {
  let cart = await request('cart.js');
  cart.items.forEach(legacyFinish);
  const maximumChanges = cart.items.length;
  let changes = 0;
  while (cart.items.some((item) => item.properties?.[legacyKey])) {
    if (changes >= maximumChanges) throw new Error('Finish verification did not complete');
    const item = cart.items.find((line) => line.properties?.[legacyKey]);
    const finish = legacyFinish(item);
    cart = await request('cart/change.js', {
      id: item.key,
      quantity: item.quantity,
      properties: finishProperties(finish.selection_id, item.properties),
    });
    changes += 1;
  }
  return { cart, changes };
}

if (typeof document !== 'undefined') {
  const routeRoot = window.Shopify?.routes?.root || '/';
  let pending;
  let failed = false;
  const verifiedForms = new WeakSet();

  const request = async (route, body) => {
    const response = await fetch(routeRoot + route, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
    });
    if (!response.ok) throw new Error('Unable to verify finish details');
    return response.json();
  };

  const showError = () => {
    failed = true;
    document.querySelectorAll('[data-csw-finish-cart-status]').forEach((status) => {
      status.textContent = 'We could not verify your finish. Please reload this page or contact us before checkout.';
      status.setAttribute('role', 'alert');
    });
  };

  const verify = () => {
    if (!pending) {
      pending = migrateLegacyCart(request).then(({ cart, changes }) => {
        failed = false;
        if (changes || document.querySelector('[data-csw-legacy-checkout]')) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            detail: { resource: cart, sourceId: 'csw-finish-privacy', data: { source: 'csw-finish-privacy', itemCount: cart.item_count } },
          }));
        }
        return cart;
      }).finally(() => { pending = null; });
    }
    return pending;
  };

  const checkExisting = () => {
    if (!failed && document.querySelector('[data-csw-legacy-finish], [data-csw-legacy-checkout]')) {
      verify().catch(showError);
    }
  };
  checkExisting();
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.sourceId !== 'csw-finish-privacy') requestAnimationFrame(checkExisting);
  });

  const continueToCheckout = async (event, proceed) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await verify();
      proceed();
    } catch {
      showError();
      if (!document.querySelector('[data-csw-finish-cart-status]')) {
        window.alert('We could not verify your finish. Please reload the cart or contact us before checkout.');
      }
    }
  };

  document.addEventListener('submit', (event) => {
    if (event.submitter?.name !== 'checkout') return;
    if (verifiedForms.has(event.target)) {
      verifiedForms.delete(event.target);
      return;
    }
    const form = event.target;
    const submitter = event.submitter;
    continueToCheckout(event, () => {
      verifiedForms.add(form);
      form.requestSubmit(submitter);
    });
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin && /\/checkout\/?$/.test(url.pathname)) {
      continueToCheckout(event, () => window.location.assign(url.href));
    }
  }, true);
}
